import PropTypes from 'prop-types';
import { kebabCase } from 'lodash';
import React from 'react';
import { conformanceSelector, enabledSelector, optionsSelector, valueSelector, labelSelector, newPathSelector } from '../selectors/index';
import ConformanceTooltip from './conformanceTooltip';
import entityComposition from '../../common/classes/entityComposition';
import Label from './label';
import OptionMultiPicker from './optionMultiPicker';
import OptionSinglePicker from './optionSinglePicker';
import OptionNestedPicker from './optionNestedPicker/optionNestedPicker';
import OptionsTablePicker from './optionsTablePicker';
import FormSearchField from './formSearchField';
import logValueOnChange from '../commonActions/logValueOnChange';
const OptionPickerField = _ref => {
  let _ref$allowsClear = _ref.allowsClear,
    allowsClear = _ref$allowsClear === void 0 ? true : _ref$allowsClear,
    _ref$allowsMultipleSe = _ref.allowsMultipleSelection,
    allowsMultipleSelection = _ref$allowsMultipleSe === void 0 ? false : _ref$allowsMultipleSe,
    _ref$allowsOther = _ref.allowsOther,
    allowsOther = _ref$allowsOther === void 0 ? false : _ref$allowsOther,
    _ref$allowsParentSele = _ref.allowsParentSelection,
    allowsParentSelection = _ref$allowsParentSele === void 0 ? false : _ref$allowsParentSele,
    _ref$className = _ref.className,
    className = _ref$className === void 0 ? '' : _ref$className,
    _ref$conformanceText = _ref.conformanceText,
    conformanceText = _ref$conformanceText === void 0 ? null : _ref$conformanceText,
    _ref$contentPath = _ref.contentPath,
    contentPath = _ref$contentPath === void 0 ? undefined : _ref$contentPath,
    enabled = _ref.enabled,
    _ref$format = _ref.format,
    format = _ref$format === void 0 ? {} : _ref$format,
    _ref$label = _ref.label,
    label = _ref$label === void 0 ? null : _ref$label,
    _ref$labelKey = _ref.labelKey,
    labelKey = _ref$labelKey === void 0 ? 'title' : _ref$labelKey,
    _ref$maxTextLength = _ref.maxTextLength,
    maxTextLength = _ref$maxTextLength === void 0 ? 2024 : _ref$maxTextLength,
    onChange = _ref.onChange,
    _ref$onFocus = _ref.onFocus,
    onFocus = _ref$onFocus === void 0 ? () => null : _ref$onFocus,
    _ref$options = _ref.options,
    options = _ref$options === void 0 ? [] : _ref$options,
    _ref$optionsTable = _ref.optionsTable,
    optionsTable = _ref$optionsTable === void 0 ? null : _ref$optionsTable,
    _ref$parentContentPat = _ref.parentContentPath,
    parentContentPath = _ref$parentContentPat === void 0 ? undefined : _ref$parentContentPat,
    path = _ref.path,
    _ref$placeholder = _ref.placeholder,
    placeholder = _ref$placeholder === void 0 ? '' : _ref$placeholder,
    _ref$testingIdentifie = _ref.testingIdentifier,
    testingIdentifier = _ref$testingIdentifie === void 0 ? '' : _ref$testingIdentifie,
    _ref$type = _ref.type,
    pickerType = _ref$type === void 0 ? undefined : _ref$type,
    _ref$value = _ref.value,
    value = _ref$value === void 0 ? '' : _ref$value,
    _ref$valueKey = _ref.valueKey,
    valueKey = _ref$valueKey === void 0 ? 'identifier' : _ref$valueKey,
    _ref$variables = _ref.variables,
    variables = _ref$variables === void 0 ? {} : _ref$variables;
  const showConformance = !!conformanceText;
  let newClassName = className;
  if (label && label.length) {
    newClassName = "t-opf t-opf-".concat(kebabCase(label), " ").concat(className);
  }
  if (showConformance) {
    newClassName += ' is-invalid';
  }
  const id = "opf-".concat(kebabCase(label));
  const displayPlaceholder = placeholder.length ? placeholder : 'Select...';

  // warning: in the sub components we're using destructuring (e.g., {...props}) which is hiding
  // prop drilling. We probably should refactor this hierarchy to use context to get settings to
  // the eventual Select component
  const newProps = {
    allowsClear,
    allowsMultipleSelection,
    allowsOther,
    allowsParentSelection,
    className: newClassName,
    enabled,
    format,
    id,
    labelKey,
    maxTextLength,
    onFocus,
    onChange,
    options,
    optionsTable,
    path,
    parentContentPath,
    pickerType,
    placeholder: displayPlaceholder,
    testingIdentifier,
    value,
    valueKey,
    variables,
    verticalPadding: false,
    contentPath
  };
  if (allowsMultipleSelection) {
    // Modify props to pass in an array of values
    newProps.values = value === '' ? [] : value;
  }
  const valueIsATree = Object.values(value || {}).some(val => val.children !== undefined);
  const isTieredPicker = options.some(option => option.options !== undefined) || valueIsATree;
  const isTablePicker = options && options.length === 0 && !!optionsTable;
  let Picker;
  if (pickerType === 'formSearchField' || pickerType === 'optionSearchField') {
    Picker = FormSearchField;
  } else if (isTablePicker) {
    Picker = OptionsTablePicker;
  } else if (isTieredPicker) {
    Picker = OptionNestedPicker;
  } else if (allowsMultipleSelection) {
    Picker = OptionMultiPicker;
  } else {
    Picker = OptionSinglePicker;
  }

  // These pickers open a modal which needs a title, we use the label
  // text for this
  if (isTablePicker || isTieredPicker) {
    newProps.label = label;
  }
  return /*#__PURE__*/React.createElement("div", {
    id: id,
    "data-testid": id
  }, /*#__PURE__*/React.createElement(Label, {
    value: label
  }), /*#__PURE__*/React.createElement(Picker, newProps), /*#__PURE__*/React.createElement(ConformanceTooltip, {
    errorText: conformanceText,
    target: id,
    placement: "right"
  }));
};
OptionPickerField.propTypes = {
  allowsClear: PropTypes.bool,
  allowsMultipleSelection: PropTypes.bool,
  allowsOther: PropTypes.bool,
  /* eslint-disable-next-line react/require-default-props */
  allowsParentSelection: PropTypes.bool,
  // defaults are assigned at a lower level
  className: PropTypes.string,
  contentPath: PropTypes.string,
  conformanceText: PropTypes.string,
  enabled: PropTypes.bool.isRequired,
  format: PropTypes.shape({}),
  label: PropTypes.string,
  labelKey: PropTypes.string,
  maxTextLength: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  optionsTable: PropTypes.string,
  parentContentPath: PropTypes.string,
  path: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  testingIdentifier: PropTypes.string,
  type: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape({})), PropTypes.shape({}), PropTypes.string]),
  valueKey: PropTypes.string,
  variables: PropTypes.shape({})
};
export { OptionPickerField as UncomposedOptionPickerField };
export default entityComposition(OptionPickerField, [], {
  conformanceText: conformanceSelector,
  enabled: enabledSelector,
  label: labelSelector,
  options: optionsSelector,
  value: valueSelector,
  onChange: logValueOnChange,
  path: newPathSelector
});